/**
 * Employee Login Component
 *
 * Email + password are verified by POST /api/auth/employee/login.
 * Terms consent is shown only after a successful password check.
 */
import React, { useState } from 'react';
import './login.css';
import systemLogo from '../../../assests/icons/system_icon.png';
import { setEmployeeSession } from '../auth/employeeAuthStorage';
import ConsentBox from '../common/consentBox/consentBox';
import Footer from '../common/footer/footer';
import {
  loginEmployee,
  logoutEmployee,
  requestEmployeePasswordReset,
} from '../../../services/employeeAuthApi';
import type { EmployeeLoginResponse } from '../../../services/employeeAuthApi';

type LoginView = 'login' | 'forgot' | 'forgot-sent';

export default function Login() {
  const [view, setView] = useState<LoginView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [pendingLogin, setPendingLogin] = useState<EmployeeLoginResponse | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await loginEmployee(email.trim(), password);
      setPendingLogin(result);
      setShowConsent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ログインに失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const message = await requestEmployeePasswordReset(email.trim());
      setInfo(message);
      setView('forgot-sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : '送信に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConsentAgree = () => {
    if (!pendingLogin?.email) {
      setShowConsent(false);
      return;
    }
    setShowConsent(false);
    setEmployeeSession(pendingLogin.email, pendingLogin.mustChangePassword);
    window.dispatchEvent(new Event('employee-auth-change'));
  };

  const handleConsentDisagree = () => {
    setShowConsent(false);
    setPendingLogin(null);
    void logoutEmployee();
  };

  const goToForgot = () => {
    setError(null);
    setInfo(null);
    setPassword('');
    setShowConsent(false);
    setView('forgot');
  };

  const goToLogin = () => {
    setError(null);
    setInfo(null);
    setView('login');
  };

  return (
    <>
    <div className={`employee-login${showConsent ? ' employee-login--blurred' : ''}`}>
      <div className="employee-login__card">
        <div className="employee-login__logo-wrap">
          <img
            src={systemLogo}
            alt=""
            className="employee-login__logo"
            aria-hidden
          />
          <h1 className="employee-login__title">書類+</h1>
        </div>

        <hr className="employee-login__divider" />

        {view === 'login' && (
          <form className="employee-login__form" onSubmit={handleSubmit}>
            <input
              type="email"
              className="employee-login__input"
              placeholder="メールアドレス"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            <input
              type="password"
              className="employee-login__input"
              placeholder="パスワード"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            {error && <p className="employee-login__error" role="alert">{error}</p>}
            <button type="submit" className="employee-login__btn" disabled={submitting}>
              {submitting ? '確認中…' : 'ログイン'}
            </button>
            <button type="button" className="employee-login__link" onClick={goToForgot}>
              パスワードをお忘れですか？
            </button>
          </form>
        )}

        {view === 'forgot' && (
          <form className="employee-login__form" onSubmit={handleForgot}>
            <p className="employee-login__hint">
              登録済みのメールアドレスを入力してください。仮パスワードを送信します。ログイン後に新しいパスワードへ変更してください。
            </p>
            <input
              type="email"
              className="employee-login__input"
              placeholder="メールアドレス"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
            {error && <p className="employee-login__error" role="alert">{error}</p>}
            <button type="submit" className="employee-login__btn" disabled={submitting}>
              {submitting ? '送信中…' : '仮パスワードを送信'}
            </button>
            <button type="button" className="employee-login__btn employee-login__btn--secondary" onClick={goToLogin}>
              ログインへ戻る
            </button>
          </form>
        )}

        {view === 'forgot-sent' && (
          <div className="employee-login__form">
            <p className="employee-login__success" role="status">
              {info || 'ご入力のメールアドレスが登録されている場合、仮パスワードを送信しました。'}
            </p>
            <p className="employee-login__hint">
              メールに記載の仮パスワードでログインし、続けて新しいパスワードを設定してください。
            </p>
            <button type="button" className="employee-login__btn" onClick={goToLogin}>
              ログインへ戻る
            </button>
          </div>
        )}

        <Footer layout="horizontal" />
      </div>
    </div>

    {showConsent && view === 'login' && (
      <ConsentBox
        translationKey="consentTerms"
        showAllLanguages
        linkUrl="/terms"
        onAgree={handleConsentAgree}
        onDisagree={handleConsentDisagree}
      />
    )}
    </>
  );
}
